import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import type { Tour } from './models'

interface TraverseDB extends DBSchema {
  tours: {
    key: string
    value: Tour
    indexes: { 'by-updated': number }
  }
  meta: {
    key: string
    value: { key: string; value: number | string }
  }
  /** offline map tiles, keyed by tile URL */
  tiles: {
    key: string
    value: { url: string; data: ArrayBuffer; contentType: string; tourId: string; savedAt: number }
    indexes: { 'by-tour': string }
  }
}

let dbPromise: Promise<IDBPDatabase<TraverseDB>> | null = null

export function db(): Promise<IDBPDatabase<TraverseDB>> {
  dbPromise ??= openDB<TraverseDB>('traverse', 1, {
    upgrade(d) {
      const tours = d.createObjectStore('tours', { keyPath: 'id' })
      tours.createIndex('by-updated', 'updatedAt')
      d.createObjectStore('meta', { keyPath: 'key' })
      const tiles = d.createObjectStore('tiles', { keyPath: 'url' })
      tiles.createIndex('by-tour', 'tourId')
    },
  })
  return dbPromise
}

export async function putTour(tour: Tour): Promise<void> {
  await (await db()).put('tours', tour)
}

export async function getTour(id: string): Promise<Tour | undefined> {
  const t = await (await db()).get('tours', id)
  return t?.deleted ? undefined : t
}

export async function listTours(): Promise<Tour[]> {
  const all = await (await db()).getAll('tours')
  return all.filter((t) => !t.deleted).sort((a, b) => b.updatedAt - a.updatedAt)
}

/** Tombstone locally; sync propagates the deletion. */
export async function deleteTour(id: string): Promise<void> {
  const d = await db()
  const t = await d.get('tours', id)
  if (t) await d.put('tours', { ...t, deleted: true, synced: false, updatedAt: Date.now() })
}

export async function dirtyTours(): Promise<Tour[]> {
  const all = await (await db()).getAll('tours')
  return all.filter((t) => !t.synced)
}

export async function getMeta(key: string): Promise<number | string | undefined> {
  return (await (await db()).get('meta', key))?.value
}

export async function setMeta(key: string, value: number | string): Promise<void> {
  await (await db()).put('meta', { key, value })
}

export async function putTile(url: string, data: ArrayBuffer, contentType: string, tourId: string): Promise<void> {
  await (await db()).put('tiles', { url, data, contentType, tourId, savedAt: Date.now() })
}

export async function getTile(url: string) {
  return (await db()).get('tiles', url)
}

export async function deleteTilesForTour(tourId: string): Promise<void> {
  const d = await db()
  const tx = d.transaction('tiles', 'readwrite')
  for (const key of await tx.store.index('by-tour').getAllKeys(tourId)) tx.store.delete(key)
  await tx.done
}

export async function tileCountForTour(tourId: string): Promise<number> {
  return (await db()).countFromIndex('tiles', 'by-tour', tourId)
}
